import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchRedditRanking,
  selectRedditRanking,
  selectRedditRankingStatus,
} from 'modules/reddit/features/redditSlice';
import { projectVisited } from 'core/features/projects/projectsSlice';
import { formatZahl, makeComparator } from 'core/utils/format';
import SortHeader from 'core/components/SortHeader';
import BackToOverview from 'core/components/BackToOverview';

/** Differenz Erwähnungen zur Messung vor 24h (null, wenn kein Vergleich vorliegt). */
const mentionsDelta = (r) =>
  r.mentions != null && r.mentions24hAgo != null ? r.mentions - r.mentions24hAgo : null;

/** Rang-Verbesserung: positiver Wert = im Ranking nach oben geklettert. */
const rangDelta = (r) =>
  r.rang != null && r.rang24hAgo != null ? r.rang24hAgo - r.rang : null;

/** Sortierwert einer Zeile je Spaltenschlüssel. */
const getValue = (r, key) => {
  if (key === 'rang') return r.rang;
  if (key === 'name') return r.name || r.ticker;
  if (key === 'mentions') return r.mentions;
  if (key === 'delta') return mentionsDelta(r);
  if (key === 'upvotes') return r.upvotes;
  if (key === 'rangDelta') return rangDelta(r);
  return null;
};

/** Farbiger Delta-Chip mit Vorzeichen (grün positiv, rot negativ). */
function DeltaChip({ wert, suffix = '' }) {
  if (wert == null) {
    return <span className="font-mono tabular-nums text-ink-muted">–</span>;
  }
  const positiv = wert > 0;
  const neutral = wert === 0;
  const farbe = neutral
    ? 'text-ink-muted'
    : positiv
    ? 'text-gain'
    : 'text-loss';
  return (
    <span className={`font-mono tabular-nums ${farbe}`}>
      {positiv ? '+' : ''}
      {formatZahl(wert, 0)}
      {suffix}
    </span>
  );
}

/**
 * Reddit-Buzz: Übersicht der aktuell meistdiskutierten Ticker (jüngster Snapshot
 * der ApeWisdom-Daten). Alle Spalten sind sortierbar; Standard ist der Rang.
 * Aktien, die auch in der Datenbank liegen, sind markiert; ein Klick öffnet den
 * Verlauf des Tickers.
 */
export default function RedditBuzzPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ranking = useSelector(selectRedditRanking);
  const status = useSelector(selectRedditRankingStatus);

  const [sortKey, setSortKey] = useState('rang');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    dispatch(projectVisited('reddit-buzz'));
    dispatch(fetchRedditRanking());
  }, [dispatch]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Rang/Name aufsteigend, Mengen/Deltas zuerst absteigend (größte oben).
      setSortDir(key === 'rang' || key === 'name' ? 'asc' : 'desc');
    }
  };

  const sichtbar = useMemo(
    () => [...ranking].sort(makeComparator((row) => getValue(row, sortKey), sortDir)),
    [ranking, sortKey, sortDir]
  );

  const stand = ranking[0]?.erfasstAm;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>

      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Reddit-Buzz</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Wie oft und intensiv über Aktien auf Reddit (r/wallstreetbets, r/stocks,
          r/investing …) diskutiert wird – Erwähnungen und Upvotes aus dem jüngsten
          Abruf. Ein Klick auf eine Zeile zeigt den Verlauf über die Zeit.
        </p>
        {stand && (
          <p className="mt-2 font-mono text-xs text-ink-muted">
            Stand: {new Date(stand).toLocaleString('de-DE')}
          </p>
        )}
      </header>

      {status === 'loading' && <p className="text-ink-muted">Lade Reddit-Erwähnungen…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Die Reddit-Erwähnungen konnten nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' && sichtbar.length === 0 && (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Daten erfasst. Sobald der Abruf gelaufen ist, erscheinen hier die
          meistdiskutierten Ticker.
        </p>
      )}

      {status === 'succeeded' && sichtbar.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-line bg-paper-soft">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line bg-paper-sunken text-left font-mono text-xs uppercase tracking-wider text-ink-muted">
                <SortHeader label="#" columnKey="rang" align="right" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Aktie" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Erwähnungen" columnKey="mentions" align="right" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Δ 24h" columnKey="delta" align="right" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Upvotes" columnKey="upvotes" align="right" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Rang Δ" columnKey="rangDelta" align="right" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((r) => (
                <tr
                  key={r.ticker}
                  onClick={() => navigate(`/projekte/reddit-buzz/${encodeURIComponent(r.ticker)}`)}
                  className="cursor-pointer border-t border-line transition-colors hover:bg-ink/[0.03]"
                >
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-muted">
                    {r.rang ?? '–'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-ink">{r.ticker}</span>
                      {r.inDatenbank && (
                        <span className="rounded-full bg-brand-500/10 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide text-brand-700 ring-1 ring-brand-500/25">
                          📊 in DB
                        </span>
                      )}
                    </div>
                    {r.name && <div className="truncate text-xs text-ink-muted">{r.name}</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-ink">
                    {r.mentions != null ? formatZahl(r.mentions, 0) : '–'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DeltaChip wert={mentionsDelta(r)} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-ink-soft">
                    {r.upvotes != null ? formatZahl(r.upvotes, 0) : '–'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DeltaChip wert={rangDelta(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status === 'succeeded' && sichtbar.length > 0 && (
        <p className="mt-3 font-mono text-xs text-ink-muted">{sichtbar.length} Ticker</p>
      )}
    </div>
  );
}
